/**
 * Accounts, by what they have been paid.
 *
 * Not the standings under another name. The standings rank by blocks won in a
 * window and answer "who is winning right now"; this ranks by tokens over the
 * whole indexed record and answers "who has been paid the most". On a chain
 * whose difficulty rose 346-fold inside its first day those are emphatically
 * not the same list — an early block cost a three-hundredth of a current one
 * and was paid accordingly.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { AccountRow } from '../api/generated/AccountRow'
import { RequestFailed, fetchAccounts } from '../api/rest'
import { exactTokens, height as fmtHeight, shortAddress, tokens } from '../lib/format'
import { href } from '../lib/routes'
import { RoleChips } from './RoleChips'

export function AccountsIndex({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  const [rows, setRows] = useState<AccountRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setRows(null)
    setError(null)
    fetchAccounts(chain, controller.signal)
      .then(setRows)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  const max = rows && rows.length > 0 ? Number(BigInt(rows[0]!.total.slice(0, 15))) : 0

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Accounts</h2>
        <span className="eyebrow">by mining rewards, all recorded history</span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && rows === null && <p className="empty">Reading the record…</p>}
      {rows !== null && rows.length === 0 && (
        <p className="empty">
          No rewards indexed for this chain yet. The event index fills outward from the tip.
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Accounts ranked by mining rewards received
            </caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col" className="left">
                  Account
                </th>
                <th scope="col">Blocks</th>
                <th scope="col">Earned</th>
                <th scope="col">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                // The same thin-evidence test the leaderboard uses, so a guess
                // is marked the same way in both places rather than reading as
                // settled here and tentative there.
                const named = row.display !== null && row.attribution !== 'preimage'
                const tentative = named && (row.attribution_votes < 3 || row.confidence < 0.6)
                // Leading digits only: these are u128 and the bar is a shape.
                const share = max > 0 ? Number(BigInt(row.total.slice(0, 15))) / max : 0
                return (
                  <tr key={row.account}>
                    <td className={`rank rank-${row.rank}`}>{row.rank}</td>
                    <td className="left">
                      <div className="miner-cell">
                        <Link
                          to={href({ chain, account: row.address })}
                          className={named ? 'miner-name' : 'miner-name anonymous'}
                          title={row.address}
                        >
                          {named ? row.display : shortAddress(row.address)}
                        </Link>
                        {/* The address as well as the name, whenever there is a
                            name. A node name is not unique to an account — one
                            node legitimately reports for several payout
                            addresses, and this page had two rows reading
                            "pearl-prover" with nothing to tell them apart. The
                            address is what the row actually is. */}
                        {named && (
                          <span className="account-address">{shortAddress(row.address)}</span>
                        )}
                        {tentative && (
                          <span
                            className="chip chip-named chip-guess"
                            title={`Resting on ${row.attribution_votes} attributed block${
                              row.attribution_votes === 1 ? '' : 's'
                            }; a later block may correct it.`}
                          >
                            node?
                          </span>
                        )}
                        <RoleChips roles={row.roles} />
                        {row.attribution === 'carried' && (
                          <span
                            className="chip chip-named chip-guess"
                            title="Name carried from another chain, matched on the same reward preimage."
                          >
                            elsewhere
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="numeral">{fmtHeight(row.blocks)}</td>
                    <td className="numeral" title={`${exactTokens(row.total, decimals)} ${symbol}`}>
                      {tokens(row.total, decimals, 2)} {symbol}
                    </td>
                    <td>
                      <div className="share-bar" aria-hidden="true">
                        <i style={{ width: `${Math.max(share * 100, 1)}%` }} />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="panel-note">
        Summed from <code>MiningRewards::MinerRewarded</code> events decoded against the runtime
        that produced each block. This is what the chain paid, not what an account holds — it says
        nothing about transfers out, and an account that has never mined does not appear however
        much it holds.
      </p>
    </section>
  )
}
